import axios from '../axiosConfig'
import { getEnv } from '@utils/env'
import { logger } from '@utils/safeLogger'

const BUSINESS_LAYER_API = getEnv('REACT_APP_BUSINESS_LAYER_BILLING', '')

interface ApiResponse<T> {
  statusCode: number
  message: string
  data: T
}

export type PatientARAnalytics = {
  total: number
  balance: number
  percentage: number
}

export enum ARAgingBucketName {
  Bucket_0_30 = '0-30',
  Bucket_31_60 = '31-60',
  Bucket_61_90 = '61-90',
  Bucket_90_Plus = '90+',
}

export enum ARAgingPayerType {
  Patient = 'patient',
  Insurance = 'insurance',
}

export type ARAgingBucketSummary = {
  bucket: ARAgingBucketName
  payerType: ARAgingPayerType
  totalCreated: number
  totalRemaining: number
  appointmentCount: number
}

export type ARAgingSummary = {
  clinicId: string
  patientId?: string
  grandTotalCreated: number
  grandTotalRemaining: number
  buckets: ARAgingBucketSummary[]
}

export type ARAgingSummaryReport = ARAgingSummary[]

export type GetPatientARAnalyticsResponse = ApiResponse<{
  analytics?: PatientARAnalytics
}>

export const getPatientARAnalytics = async (
  patientId?: string,
  clinicId?: string,
): Promise<GetPatientARAnalyticsResponse> => {
  try {
    const params: { patientId?: string; clinicId?: string } = {}
    if (patientId) {
      params.patientId = patientId
    }
    if (clinicId) {
      params.clinicId = clinicId
    }
    const response = await axios.get<GetPatientARAnalyticsResponse>(
      `${BUSINESS_LAYER_API}/api/v1/analytics/patient-ar`,
      {
        params,
      },
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in getPatientARAnalytics:', e)
    throw e
  }
}

export const getInsuranceARAnalytics = async (
  patientId?: string,
  clinicId?: string,
): Promise<GetPatientARAnalyticsResponse> => {
  try {
    const params: { patientId?: string; clinicId?: string } = {}
    if (patientId) {
      params.patientId = patientId
    }
    if (clinicId) {
      params.clinicId = clinicId
    }
    const response = await axios.get<GetPatientARAnalyticsResponse>(
      `${BUSINESS_LAYER_API}/api/v1/analytics/insurance-ar`,
      {
        params,
      },
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in getInsuranceARAnalytics:', e)
    throw e
  }
}

export type GetARAgingResponse = ApiResponse<{
  report?: ARAgingSummaryReport
}>

export type SidePanelAnalytics = {
  insuranceBalance: number
  patientBalance: number
  familyBalance: number
  discountApplied: number
  insuranceTotal: number
  patientTotal: number
  outstandingTotal: number
  walletBalance: number
}

export type GetSidePanelAnalyticsResponse = ApiResponse<{
  analytics?: SidePanelAnalytics
}>

export const getARAging = async (
  patientId?: string,
  clinicId?: string,
): Promise<GetARAgingResponse> => {
  try {
    const params: { patientId?: string; clinicId?: string } = {}
    if (patientId) {
      params.patientId = patientId
    }
    if (clinicId) {
      params.clinicId = clinicId
    }
    const response = await axios.get<GetARAgingResponse>(
      `${BUSINESS_LAYER_API}/api/v1/analytics/ar-aging`,
      {
        params,
      },
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in getARAging:', e)
    throw e
  }
}

export const getSidePanelAnalytics = async (
  patientId: string,
  clinicId?: string,
): Promise<GetSidePanelAnalyticsResponse> => {
  try {
    const params: { patientId: string; clinicId?: string } = { patientId }
    if (clinicId) {
      params.clinicId = clinicId
    }
    const response = await axios.get<GetSidePanelAnalyticsResponse>(
      `${BUSINESS_LAYER_API}/api/v1/analytics/side-panel`,
      {
        params,
      },
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in getSidePanelAnalytics:', e)
    throw e
  }
}
